// CATALOG-SEARCH-119B — same-origin identity overlay (not a second catalog).
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { fetchSameOriginJsonFile } from "./catalog-static-json-v2.js";
import { formatUah, roundUpUah } from "./money-v2.js";
import {
  normalizeIdentifier,
  resolveBarcodeVerification,
  resolveDisplayTitle,
  type BarcodeVerificationState,
} from "./product-identity-v2.js";

export interface IdentityOverlayRow {
  prom_product_id?: string;
  merchant_sku?: string | null;
  merchant_article?: string | null;
  barcode_gtin?: string | null;
  barcode_verification?: BarcodeVerificationState | null;
  manufacturer_part_number?: string | null;
  warehouse_code?: string | null;
  source_system?: string | null;
  source_updated_at?: string | null;
  last_synced_at?: string | null;
  source_title?: string | null;
  customer_title?: string | null;
  title_aliases?: string[];
  /** Staging-only authoritative price patch when Prom evidence is fresher than snapshot. */
  price_uah_patch?: number | null;
  price_patch_source?: string | null;
}

export interface IdentityOverlayFile {
  schema?: string;
  version?: string;
  products?: Record<string, IdentityOverlayRow>;
}

let overlayCache: Promise<IdentityOverlayFile> | null = null;

function isIdentityOverlayFile(json: unknown): json is IdentityOverlayFile {
  return Boolean(json && typeof json === "object" && (json as IdentityOverlayFile).products);
}

async function loadOverlay(): Promise<IdentityOverlayFile> {
  if (!overlayCache) {
    overlayCache = (async () => {
      const file = await fetchSameOriginJsonFile(
        "catalog-identity-overlay-v2.json",
        isIdentityOverlayFile,
      );
      return file ?? { products: {} };
    })();
  }
  return overlayCache;
}

export function applyIdentityOverlayToProduct(
  product: CatalogV2Product,
  row: IdentityOverlayRow | null | undefined,
): CatalogV2Product {
  if (!row) return product;
  const merchantSku = row.merchant_sku?.trim() || product.merchantSku;
  const merchantArticle = row.merchant_article?.trim() || product.merchantArticle;
  const barcodeGtin = row.barcode_gtin?.trim() || product.barcodeGtin;
  const barcodeVerification =
    row.barcode_verification ||
    resolveBarcodeVerification(barcodeGtin, Boolean(barcodeGtin)) ||
    product.barcodeVerification;
  const sourceTitle = row.source_title?.trim() || product.sourceTitle || product.title;
  const customerTitle = row.customer_title?.trim() || product.customerTitle;
  const titles = resolveDisplayTitle({ sourceTitle, customerTitle });
  const aliases = [
    ...new Set([...(product.titleAliases ?? []), ...(row.title_aliases ?? [])].map((a) => a.trim()).filter(Boolean)),
  ];

  let priceUah = product.priceUah;
  let displayPrice = product.displayPrice;
  let isPriced = product.isPriced;
  const patch = row.price_uah_patch;
  if (typeof patch === "number" && Number.isFinite(patch) && patch >= 1) {
    // Never apply bank-canary / fractional patches onto commercial Product.price.
    if (product.priceUah == null || patch !== product.priceUah) {
      priceUah = roundUpUah(patch);
      displayPrice = formatUah(priceUah);
      isPriced = true;
    }
  }

  return {
    ...product,
    promProductId: row.prom_product_id?.trim() || product.promProductId || product.id,
    merchantSku: merchantSku || null,
    merchantArticle: merchantArticle || null,
    barcodeGtin: barcodeGtin || null,
    barcodeVerification: barcodeVerification ?? "absent",
    manufacturerPartNumber: row.manufacturer_part_number?.trim() || product.manufacturerPartNumber,
    warehouseCode: row.warehouse_code?.trim() || product.warehouseCode,
    sourceSystem: row.source_system?.trim() || product.sourceSystem,
    sourceUpdatedAt: row.source_updated_at?.trim() || product.sourceUpdatedAt,
    lastSyncedAt: row.last_synced_at?.trim() || product.lastSyncedAt,
    sourceTitle: titles.source,
    customerTitle: customerTitle || null,
    titleAliases: aliases,
    title: titles.display,
    // Keep legacy sku as merchant code when known; never invent from Prom id for customer label path.
    sku: merchantSku || merchantArticle || product.sku,
    priceUah,
    displayPrice,
    isPriced,
    isSellable: isPriced ? product.isSellable || product.stockStatus !== "out_of_stock" : product.isSellable,
  };
}

export async function enrichCatalogWithIdentityOverlay(
  products: CatalogV2Product[],
): Promise<CatalogV2Product[]> {
  const file = await loadOverlay();
  const map = file.products ?? {};
  return products.map((p) => applyIdentityOverlayToProduct(p, map[p.id] ?? map[normalizeIdentifier(p.id)]));
}

export function resetIdentityOverlayCacheForTests(): void {
  overlayCache = null;
}
